// src/components/StrategyBuilder/StepConfirmation.jsx
import React from 'react';
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Stack,
  Chip,
  Card,
  CardContent,
  Alert,
  Paper,
  Divider,
  alpha,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  InfoOutlined as InfoIcon,
  FlashOn as FlashIcon,
  CompareArrows as SequenceIcon,
  Block as InterferenceIcon,
  HelpOutline as UnknownIcon,
  TrendingUp as LongIcon,
  TrendingDown as ShortIcon,
} from '@mui/icons-material';

const StepConfirmation = ({ data, onChange }) => {
  const conditions = data.conditions || [];
  const selectedCondition = conditions[data.confirmation_condition_index];
  
  const getConditionIcon = (type) => {
    if (!type) return UnknownIcon;
    switch (type) {
      case 'simultaneous':
        return FlashIcon;
      case 'time_bound':
        return SequenceIcon;
      case 'interference':
        return InterferenceIcon;
      default:
        return CheckIcon;
    }
  };

  const getConditionColor = (type) => {
    if (!type) return { main: '#94a3b8', bg: '#f1f5f9', border: '#cbd5e1' };
    switch (type) {
      case 'simultaneous':
        return { main: '#3b82f6', bg: '#eff6ff', border: '#bfdbfe' };
      case 'time_bound':
        return { main: '#8b5cf6', bg: '#f5f3ff', border: '#e9d5ff' };
      case 'interference':
        return { main: '#ef4444', bg: '#fef2f2', border: '#fecaca' };
      default:
        return { main: '#667eea', bg: '#eff6ff', border: '#c7d2fe' };
    }
  };

  const getConditionTypeLabel = (type) => {
    if (!type) return 'Not configured';
    if (type === 'time_bound') return 'Time Bound';
    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const SignalChip = ({ signal }) => {
    if (!signal || !signal.algo) return null;
    const isLong = signal.direction === 'LONG';
    const DirectionIcon = isLong ? LongIcon : ShortIcon;
    const directionColor = isLong ? '#10b981' : '#ef4444';
    const directionBg = isLong ? '#d1fae5' : '#fee2e2';
    
    return (
      <Chip
        icon={<DirectionIcon sx={{ fontSize: '12px !important' }} />}
        label={`${signal.algo}:${signal.direction}`}
        size="small"
        sx={{
          bgcolor: directionBg,
          color: directionColor,
          fontWeight: 600,
          fontFamily: 'monospace',
          fontSize: '0.7rem',
          height: 20,
          '& .MuiChip-icon': {
            color: directionColor,
          },
        }}
      />
    );
  };

  const renderConditionSummary = (condition) => {
    if (condition.type === 'simultaneous') {
      return (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
          {condition.signals?.map((signal, idx) => (
            <SignalChip key={idx} signal={signal} />
          ))}
        </Stack>
      );
    }
    
    if (condition.type === 'time_bound' || condition.type === 'interference') {
      return (
        <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
          <SignalChip signal={condition.trigger} />
          <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mx: 0.5 }}>→</Typography>
          <SignalChip signal={condition.target} />
        </Stack>
      );
    }
    
    return null;
  };

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CheckIcon sx={{ color: 'white', fontSize: 28 }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 800, 
              color: '#1e293b',
              mb: 0.5,
            }}
          >
            Confirmation Condition
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select which condition must complete last to trigger the alert
          </Typography>
        </Box>
      </Stack>

      <Divider sx={{ mb: 4 }} />

      {/* Info Alert */}
      <Alert 
        icon={<InfoIcon />}
        severity="info" 
        sx={{ 
          mb: 4, 
          borderRadius: 2,
          bgcolor: '#eff6ff',
          border: '1px solid #bfdbfe',
          '& .MuiAlert-icon': {
            color: '#3b82f6',
          },
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5, color: '#1e3a8a' }}>
          How Confirmation Works
        </Typography>
        <Typography variant="body2" sx={{ color: '#1e40af' }}>
          Choose which condition must complete <strong>last</strong> to confirm the alert. All other conditions can complete in any order, but the confirmation condition must be the final trigger.
        </Typography>
      </Alert>

      {/* Confirmation Condition Selection */}
      <Card 
        sx={{ 
          borderRadius: 3, 
          border: '2px solid',
          borderColor: selectedCondition ? '#10b981' : '#e2e8f0',
          boxShadow: selectedCondition 
            ? '0 8px 24px rgba(16, 185, 129, 0.15)'
            : '0 2px 8px rgba(0,0,0,0.04)',
          transition: 'all 0.3s',
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                bgcolor: '#d1fae5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CheckIcon sx={{ color: '#059669', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
                Select Confirmation Condition
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This condition will be the final trigger
              </Typography>
            </Box>
          </Stack>

          <FormControl fullWidth>
            <Select
              value={data.confirmation_condition_index ?? ''}
              onChange={(e) => onChange({ ...data, confirmation_condition_index: e.target.value })}
              displayEmpty
              sx={{
                borderRadius: 2,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderWidth: 2,
                  borderColor: '#e2e8f0',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#cbd5e1',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#10b981',
                  borderWidth: 2,
                },
              }}
              renderValue={(selected) => {
                const condition = conditions[selected];
                if (condition === undefined) {
                  return <em style={{ color: '#94a3b8' }}>Select a confirmation condition...</em>;
                }
                
                const Icon = getConditionIcon(condition.type);
                const colors = getConditionColor(condition.type);
                
                return (
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '8px',
                        bgcolor: colors.bg,
                        border: `2px solid ${colors.main}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon sx={{ color: colors.main, fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}>
                        {condition.name || `Condition ${selected + 1}`}
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: '#64748b' }}>
                        {getConditionTypeLabel(condition.type)} Condition
                      </Typography>
                    </Box>
                  </Stack>
                );
              }}
            >
              {conditions.length === 0 ? (
                <MenuItem disabled>
                  <Typography sx={{ color: '#94a3b8', fontStyle: 'italic' }}>
                    No conditions available
                  </Typography>
                </MenuItem>
              ) : (
                conditions.map((condition, index) => {
                  const Icon = getConditionIcon(condition.type);
                  const colors = getConditionColor(condition.type);
                  const isSelected = data.confirmation_condition_index === index;
                  
                  return (
                    <MenuItem 
                      key={condition.id || index} 
                      value={index}
                      sx={{
                        py: 1.5,
                        px: 2,
                        borderRadius: 1,
                        mb: 0.5,
                        mx: 0.5,
                        bgcolor: isSelected ? alpha(colors.main, 0.08) : 'transparent',
                        '&:hover': {
                          bgcolor: alpha(colors.main, 0.12),
                        },
                      }}
                    >
                      <Stack spacing={0.5} sx={{ width: '100%' }}>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '10px',
                              bgcolor: colors.bg,
                              border: `2px solid ${isSelected ? colors.main : colors.border}`,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s',
                            }}
                          >
                            <Icon sx={{ color: colors.main, fontSize: 22 }} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography sx={{ fontWeight: 700, color: '#1e293b', mb: 0.25 }}>
                              {condition.name || `Condition ${index + 1}`}
                            </Typography>
                            <Chip
                              label={getConditionTypeLabel(condition.type)}
                              size="small"
                              sx={{
                                bgcolor: colors.bg,
                                color: colors.main,
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                height: 20,
                              }}
                            />
                          </Box>
                          {isSelected && (
                            <CheckIcon sx={{ color: '#10b981', fontSize: 24 }} />
                          )}
                        </Stack>
                        {renderConditionSummary(condition)}
                      </Stack>
                    </MenuItem>
                  );
                })
              )}
            </Select>
          </FormControl>

          {/* Selected Condition Display */}
          {selectedCondition && (
            <Paper
              elevation={0}
              sx={{
                mt: 3,
                p: 2.5,
                borderRadius: 2,
                bgcolor: '#d1fae5',
                border: '2px solid #a7f3d0',
              }}
            >
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <CheckIcon sx={{ color: '#059669', mt: 0.3 }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#065f46', mb: 0.5 }}>
                    Confirmation Set
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#047857' }}>
                    <strong>{selectedCondition.name || 'This condition'}</strong> must be the last condition to complete before the alert triggers.
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          )}
        </CardContent>
      </Card>

      {/* Info Box */}
      {conditions.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            mt: 4,
            p: 3,
            bgcolor: '#fef3c7',
            border: '1px solid #fde68a',
            borderRadius: 2,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <InfoIcon sx={{ color: '#d97706', mt: 0.3 }} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#92400e', mb: 0.5 }}>
                Strategy Execution Order
              </Typography>
              <Typography variant="body2" sx={{ color: '#78350f' }}>
                All conditions must be satisfied, but the confirmation condition determines when the alert is triggered. Other conditions can fire in any order.
              </Typography>
            </Box>
          </Stack>
        </Paper>
      )}
    </Box>
  );
};

export default StepConfirmation;
